import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import QuickMessageButton from './QuickMessageButton';
import { toast } from 'sonner';

const ActionButtons = ({ 
  onView, 
  onEdit, 
  onDelete, 
  onStatusChange,
  onMessage,
  item, // Pass the item data for messaging
  itemType, // Pass the item type (assignment/class)
  currentStatus,
  showView = true,
  showEdit = true,
  showDelete = true,
  showStatus = true,
  showMessage = true,
  size = 'md'
}) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const statusOptions = [
    { value: 'pending', label: 'Pending', icon: 'Clock' },
    { value: 'in-progress', label: 'In Progress', icon: 'Play' },
    { value: 'completed', label: 'Completed', icon: 'CheckCircle' },
    { value: 'cancelled', label: 'Cancelled', icon: 'XCircle' },
  ];

  const sizeConfig = {
    sm: {
      button: 'p-2 min-h-[2rem] min-w-[2rem]',
      icon: 14
    },
    md: {
      button: 'p-2.5 min-h-[2.5rem] min-w-[2.5rem]',
      icon: 16
    },
    lg: {
      button: 'p-3 min-h-[3rem] min-w-[3rem]',
      icon: 18
    }
  };

  const sizing = sizeConfig[size];

  const handleDelete = async () => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      await onDelete(item);
      toast.success('Item deleted successfully');
    } catch (error) {
      toast.error('Failed to delete item');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await onStatusChange(newStatus);
      toast.success(`Status changed to ${newStatus}`);
      setShowStatusMenu(false);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="flex items-center space-x-2">
      {showView && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click from triggering
            onView();
          }}
          className={`
            ${sizing.button} rounded-lg bg-blue-500/20 hover:bg-blue-500/30 
            text-blue-400 hover:text-blue-300 transition-colors
            backdrop-blur-sm border border-blue-500/30
          `}
          title="View Details"
        >
          <Icon name="Eye" size={sizing.icon} />
        </motion.button>
      )}

      {showEdit && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click from triggering
            onEdit();
          }}
          className={`
            ${sizing.button} rounded-lg bg-green-500/20 hover:bg-green-500/30 
            text-green-400 hover:text-green-300 transition-colors
            backdrop-blur-sm border border-green-500/30
          `}
          title="Edit"
        >
          <Icon name="Edit" size={sizing.icon} />
        </motion.button>
      )}

      {showStatus && (
        <div className="relative">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click from triggering
              setShowStatusMenu(!showStatusMenu);
            }}
            className={`
              ${sizing.button} rounded-lg bg-purple-500/20 hover:bg-purple-500/30 
              text-purple-400 hover:text-purple-300 transition-colors
              backdrop-blur-sm border border-purple-500/30
            `}
            title="Change Status"
          >
            <Icon name="MoreHorizontal" size={sizing.icon} />
          </motion.button>

          {showStatusMenu && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -10 }}
              className="absolute right-0 mt-2 w-48 bg-gray-900/95 backdrop-blur-sm border border-white/20 rounded-lg shadow-xl z-50"
            >
              <div className="py-1">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleStatusChange(option.value)}
                    className={`
                      w-full px-4 py-2 text-left text-sm text-white/80 hover:bg-white/10 
                      transition-colors flex items-center space-x-2
                      ${currentStatus === option.value ? 'bg-white/10 text-white' : ''}
                    `}
                  >
                    <Icon name={option.icon} size={14} />
                    <span>{option.label}</span>
                    {currentStatus === option.value && (
                      <Icon name="Check" size={14} className="ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      )}

      {showMessage && item && itemType && (
        <div onClick={(e) => e.stopPropagation()}>
          <QuickMessageButton 
            item={item} 
            itemType={itemType} 
            onMessageSent={onMessage}
          />
        </div>
      )}

      {showDelete && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click from triggering
            handleDelete();
          }}
          disabled={isDeleting}
          className={`
            ${sizing.button} rounded-lg bg-red-500/20 hover:bg-red-500/30 
            text-red-400 hover:text-red-300 transition-colors
            backdrop-blur-sm border border-red-500/30
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
          title="Delete"
        >
          {isDeleting ? (
            <Icon name="Loader2" size={sizing.icon} className="animate-spin" />
          ) : (
            <Icon name="Trash2" size={sizing.icon} />
          )}
        </motion.button>
      )}

      {/* Click outside to close status menu */}
      {showStatusMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowStatusMenu(false)}
        />
      )}
    </div>
  );
};

export default ActionButtons;